import { useParams, Link } from "react-router-dom";
import type { ReactNode } from "react";
import { ArrowLeft, Image, DollarSign, Users, Tag, Flag, StickyNote } from "lucide-react";
import PageHeader from "../components/ui/PageHeader";
import Card from "../components/ui/Card";
import { getCampanha, campInicio, campFim, campTipos, TIPO_META, MUDANCA_META, type MudancaTipo } from "../data/campaignTimeline";

const MESES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
function fmtLong(d: string): string {
  const p = d.split("-");
  return `${p[2]} ${MESES[Number(p[1]) - 1]} ${p[0]}`;
}
function fmtCurto(d: string): string {
  const p = d.split("-");
  return `${p[2]}/${p[1]}/${p[0]}`;
}
function fmt(d: string): string {
  const p = d.split("-");
  return `${p[2]}/${p[1]}`;
}
function parse(d: string): Date {
  const p = d.split("-");
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
}
function dias(a: Date, b: Date): number {
  return Math.round((b.getTime() - a.getTime()) / 86400000);
}

const ICONE: Record<MudancaTipo, ReactNode> = {
  criativo: <Image size={13} strokeWidth={2.2} />,
  budget: <DollarSign size={13} strokeWidth={2.2} />,
  publico: <Users size={13} strokeWidth={2.2} />,
  oferta: <Tag size={13} strokeWidth={2.2} />,
  status: <Flag size={13} strokeWidth={2.2} />,
  nota: <StickyNote size={13} strokeWidth={2.2} />,
};

const STATUS_LABEL: Record<string, string> = { planejado: "Planejado", ativo: "Ativo", concluido: "Concluído", pausado: "Pausado" };

function Info({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] font-semibold text-esm-gray-400 uppercase tracking-wide">{label}</div>
      <div className="text-sm text-esm-gray-900 mt-0.5">{children}</div>
    </div>
  );
}

export default function TimelineCampanha() {
  const { id } = useParams<{ id: string }>();
  const c = id ? getCampanha(id) : undefined;

  if (!c) {
    return (
      <div className="animate-fade-in">
        <PageHeader title="Campanha não encontrada" subtitle="O identificador informado não existe no plano." />
        <Link to="/timeline" className="inline-flex items-center gap-1.5 text-sm font-semibold text-esm-brown-700 hover:text-esm-brown-900">
          <ArrowLeft size={16} /> Voltar para a timeline
        </Link>
      </div>
    );
  }

  const tipos = campTipos(c);
  const cIni = campInicio(c);
  const cFim = campFim(c);
  const corPrincipal = TIPO_META[c.fases[0]?.tipo ?? "vendas"].cor;
  const mudancas = [...(c.mudancas ?? [])].sort((a, b) => b.data.localeCompare(a.data));

  // Mini linha do tempo horizontal (fases + marcos das alterações)
  const rs = new Date(parse(cIni).getFullYear(), parse(cIni).getMonth(), 1);
  const re = new Date(parse(cFim).getFullYear(), parse(cFim).getMonth() + 1, 0);
  const totalDays = dias(rs, re) + 1;
  const PX = Math.min(22, Math.max(4, Math.round(820 / totalDays)));
  const larg = totalDays * PX;
  const meses: Array<{ label: string; off: number; days: number }> = [];
  let cur = new Date(rs);
  while (cur <= re) {
    const dim = new Date(cur.getFullYear(), cur.getMonth() + 1, 0).getDate();
    meses.push({ label: `${MESES[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}`, off: dias(rs, cur), days: dim });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  const hoje = new Date();
  hoje.setHours(0, 0, 0, 0);
  const hojeOff = dias(rs, hoje);
  const opac = c.status === "concluido" ? 0.55 : c.status === "planejado" ? 0.8 : 1;
  const LANE = 26;
  const fasesH = c.fases.length * LANE;
  const trackH = fasesH + 24;

  return (
    <div className="animate-fade-in">
      <PageHeader
        title={c.nome}
        subtitle={`${tipos.map((t) => TIPO_META[t].label).join(" + ")} · ${c.plataforma} · ${fmtCurto(cIni)} → ${fmtCurto(cFim)}`}
        icon={<span className="w-3.5 h-3.5 rounded-full" style={{ background: corPrincipal }} />}
        actions={
          <Link to="/timeline" className="inline-flex items-center gap-1.5 text-sm font-semibold text-esm-gray-600 hover:text-esm-gray-900 border border-esm-gray-200 hover:border-esm-gray-300 rounded-lg px-3 py-1.5 bg-white transition-colors">
            <ArrowLeft size={16} /> Voltar
          </Link>
        }
      />

      <Card className="mb-5">
        <div className="flex flex-wrap gap-x-10 gap-y-4 pt-5">
          <Info label="Fases">
            <span className="inline-flex items-center gap-x-2 flex-wrap">
              {tipos.map((t) => (
                <span key={t} className="inline-flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full" style={{ background: TIPO_META[t].cor }} />
                  {TIPO_META[t].label}
                </span>
              ))}
            </span>
          </Info>
          <Info label="Status">{STATUS_LABEL[c.status] ?? c.status}</Info>
          <Info label="Plataforma">{c.plataforma}</Info>
          <Info label="Período">{fmtCurto(cIni)} → {fmtCurto(cFim)}</Info>
          {c.oferta && <Info label="Oferta">{c.oferta}</Info>}
          {c.cupom && (
            <Info label="Cupom">
              <span className="font-mono text-[13px] bg-esm-cream-100 border border-esm-gray-100 rounded px-1.5 py-0.5">{c.cupom}</span>
            </Info>
          )}
        </div>
        {c.nota && <p className="text-[13px] text-esm-gray-500 mt-4 pt-4 border-t border-esm-gray-50">{c.nota}</p>}
      </Card>

      <Card title="Linha do tempo" subtitle="Fases da campanha e marcos das alterações" className="mb-5">
        <div className="overflow-x-auto pt-1">
          <div style={{ minWidth: larg }}>
            <div className="relative h-6 mb-1" style={{ width: larg }}>
              {meses.map((m, i) => (
                <div key={i} className="absolute top-0 h-full border-l border-esm-gray-100 pl-1.5 text-[11px] font-semibold text-esm-gray-400 capitalize" style={{ left: m.off * PX, width: m.days * PX }}>
                  {m.label}
                </div>
              ))}
            </div>
            <div className="relative" style={{ width: larg, height: trackH }}>
              {meses.map((m, i) => (
                <div key={i} className="absolute inset-y-0 border-l border-esm-gray-100" style={{ left: m.off * PX }} />
              ))}
              {hojeOff >= 0 && hojeOff <= totalDays && (
                <div className="absolute inset-y-0 border-l-2 border-esm-error z-20" style={{ left: hojeOff * PX }}>
                  <span className="absolute top-0 left-1 text-[9px] font-bold text-esm-error">hoje</span>
                </div>
              )}
              {/* Fases */}
              {c.fases.map((f, fi) => {
                const off = dias(rs, parse(f.inicio)) * PX;
                const w = Math.max((dias(parse(f.inicio), parse(f.fim)) + 1) * PX, 8);
                const fm = TIPO_META[f.tipo];
                return (
                  <div
                    key={fi}
                    className="absolute rounded-md flex items-center px-2 shadow-sm"
                    style={{ left: off, width: w, top: fi * LANE + 2, height: LANE - 6, background: fm.cor, opacity: opac }}
                    title={`${fm.label}: ${fmt(f.inicio)}–${fmt(f.fim)}`}
                  >
                    <span className="text-[9.5px] font-semibold text-white whitespace-nowrap overflow-hidden">{fm.label}</span>
                  </div>
                );
              })}
              {/* Marcos das alterações (na base) */}
              <div className="absolute left-0 right-0 border-t border-esm-gray-100" style={{ top: fasesH + 6 }} />
              {(c.mudancas ?? []).map((m, i) => {
                const mm = MUDANCA_META[m.tipo];
                return (
                  <span
                    key={i}
                    className="absolute w-3 h-3 rounded-full ring-2 ring-white z-10"
                    style={{ left: dias(rs, parse(m.data)) * PX, top: fasesH + 6, transform: "translate(-50%, -50%)", background: mm.cor }}
                    title={`${fmtCurto(m.data)} · ${mm.label}: ${m.titulo}`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </Card>

      <Card title="Histórico de alterações" subtitle={`${mudancas.length} registro${mudancas.length === 1 ? "" : "s"} — mais recentes primeiro`}>
        {mudancas.length === 0 ? (
          <div className="text-sm text-esm-gray-400 py-6 text-center">
            Nenhuma alteração registrada ainda.
            <br />
            <span className="text-[12px]">Registre em <code className="text-esm-gray-500">apps/dashboard/src/data/campaignTimeline.ts</code> (campo <code className="text-esm-gray-500">mudancas</code>).</span>
          </div>
        ) : (
          <ol className="relative pt-1">
            <div className="absolute left-[9px] top-2 bottom-2 w-px bg-esm-gray-100" />
            {mudancas.map((m, i) => {
              const mm = MUDANCA_META[m.tipo];
              return (
                <li key={i} className="relative pl-8 pb-5 last:pb-0">
                  <span className="absolute left-0 top-0.5 w-[19px] h-[19px] rounded-full flex items-center justify-center text-white ring-4 ring-white" style={{ background: mm.cor }}>
                    {ICONE[m.tipo]}
                  </span>
                  <div className="text-[11px] font-semibold text-esm-gray-400">{fmtLong(m.data)}</div>
                  <div className="mt-0.5 flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: mm.cor, background: `${mm.cor}1a` }}>
                      {mm.label}
                    </span>
                    <span className="text-sm font-semibold text-esm-gray-900">{m.titulo}</span>
                  </div>
                  {m.detalhe && <div className="text-[13px] text-esm-gray-500 mt-1 leading-snug">{m.detalhe}</div>}
                </li>
              );
            })}
          </ol>
        )}
      </Card>
    </div>
  );
}
